import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as THREE from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import styles from "./Hero.module.css";

const MM_TO_SCENE = 1 / 280;
const QPOS_TO_SCENE = 1 / (25 * 280);
// Eje real del rodillo colector en CAD: Y = 342.4 mm, Z = 400.5 mm
const DRUM_AXIS_Y = (342.4 - 315) * MM_TO_SCENE;
const DRUM_AXIS_Z = (400.5 - 305) * MM_TO_SCENE;
// Ejes reales de los 2 ventiladores laterales de refrigeración en CAD (X = 11.8..32.4 mm)
const FAN1_AXIS_Y = (112.37 - 315) * MM_TO_SCENE;
const FAN1_AXIS_Z = (176.75 - 305) * MM_TO_SCENE;
const FAN2_AXIS_Y = (112.37 - 315) * MM_TO_SCENE;
const FAN2_AXIS_Z = (416.75 - 305) * MM_TO_SCENE;

const CAMERA_PRESETS = {
  orbit:     { pos: null, look: [0.02, -0.02, 0] },
  spray:     { pos: [-1.15, 0.76, -1.95], look: [0.48, 0.12, 0.18] },
  isoFront:  { pos: [-2.95, 2.22, 5.55], look: [0.04, -0.02, 0] },
  isoBack:   { pos: [-3.20, 1.92, -5.40], look: [-0.04, -0.02, 0] },
  front:     { pos: [0.0, 0.08, 6.30], look: [0.0, -0.02, 0] },
  panel:     { pos: [-1.12, -0.48, 2.25], look: [-1.12, -0.54, 0.98] },
  sideRight: { pos: [5.75, 0.04, 0.02], look: [0.5, 0.0, 0.02] }
};

const CAMERA_TABS = [
  { id: "orbit", label: "360°" },
  { id: "spray", label: "Spray" },
  { id: "isoFront", label: "Iso Frontal" },
  { id: "isoBack", label: "Iso Posterior" },
  { id: "front", label: "Frontal" },
  { id: "panel", label: "Panel LCD" },
  { id: "sideRight", label: "Módulo HV" }
];

const CAMERA_LABELS = {
  orbit: "Órbita continua 360° — Cabina de electrohilado AYA.",
  spray: "Primer plano del cono de Taylor y proyección de nanofibras sobre el colector.",
  isoFront: "Isométrica frontal — Indicador verde encendido, pantalla LCD con telemetría y logo AYA luminoso.",
  isoBack: "Isométrica posterior — Carro lineal, spray de electrohilado y ventiladores.",
  front: "Vista frontal — Paneles de mando iluminados, cámara de trabajo y logo AYA con resplandor.",
  panel: "Detalle del panel de control — Pantalla LCD azul (RPM del colector y velocidad m/s de aguja) y luz piloto verde.",
  sideRight: "Módulo lateral derecho — Cubierta acrílica y fuente de alta tensión interna."
};

function createBrushedDrumRoughnessMap() {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 512;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#262626";
  ctx.fillRect(0, 0, 256, 512);
  for (let y = 0; y < 512; y += 2) {
    const v = 30 + Math.floor(Math.sin(y * 0.08) * 18 + Math.cos(y * 0.23) * 14);
    ctx.fillStyle = `rgb(${v},${v},${v})`;
    ctx.fillRect(0, y, 256, 2);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

function createSprayParticleTexture() {
  const c = document.createElement("canvas");
  c.width = 64;
  c.height = 64;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, "rgba(255, 255, 255, 1)");
  g.addColorStop(0.35, "rgba(180, 245, 205, 0.92)");
  g.addColorStop(0.7, "rgba(63, 163, 40, 0.48)");
  g.addColorStop(1, "rgba(63, 163, 40, 0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

// Textura radial suave para los resplandores (piloto verde frontal, logo AYA y retroiluminación LCD)
function createGlowHaloTexture(innerRgba, midRgba, outerRgba = "rgba(0,0,0,0)") {
  const c = document.createElement("canvas");
  c.width = 128;
  c.height = 128;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(64, 64, 2, 64, 64, 62);
  g.addColorStop(0, innerRgba);
  g.addColorStop(0.38, midRgba);
  g.addColorStop(1, outerRgba);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

// Crea y actualiza el CanvasTexture de la pantalla LCD azul frontal con RPM del tambor y m/s de la aguja
function createLcdDisplayController(textures) {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 256;
  const ctx = canvas.getContext("2d");
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  textures.add(texture);

  let lastDrawTime = -1;

  function draw(elapsed, boost) {
    // Refrescar ~10 veces por segundo como un controlador LCD industrial real
    if (elapsed - lastDrawTime < 0.09 && lastDrawTime >= 0) return;
    lastDrawTime = elapsed;

    // Fondo azul clásico de pantalla LCD retroiluminada
    const bgGrad = ctx.createLinearGradient(0, 0, 0, 256);
    bgGrad.addColorStop(0, "#0028e8");
    bgGrad.addColorStop(0.5, "#0035ff");
    bgGrad.addColorStop(1, "#001ec4");
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, 512, 256);

    // Retícula sutil de matriz de píxeles LCD
    ctx.fillStyle = "rgba(160, 220, 255, 0.06)";
    for (let y = 8; y < 248; y += 6) {
      ctx.fillRect(10, y, 492, 2);
    }

    // Marco interior luminoso de la pantalla
    ctx.strokeStyle = "rgba(150, 225, 255, 0.45)";
    ctx.lineWidth = 3;
    ctx.strokeRect(10, 10, 492, 236);

    // Encabezado de estado del sistema
    ctx.fillStyle = "rgba(200, 240, 255, 0.85)";
    ctx.font = "600 22px 'JetBrains Mono', monospace";
    ctx.fillText("AYA ELECTROSPIN // TELEMETRÍA", 26, 44);

    // Indicador parpadeante "RUN"
    const blink = Math.sin(elapsed * 6) > -0.2;
    ctx.fillStyle = blink ? "#7dff96" : "#288855";
    ctx.beginPath();
    ctx.arc(472, 36, 8, 0, Math.PI * 2);
    ctx.fill();

    // Línea separadora
    ctx.fillStyle = "rgba(150, 225, 255, 0.35)";
    ctx.fillRect(24, 58, 464, 2);

    // Cálculo de telemetría en vivo:
    // 1) RPM del cilindro colector giratorio
    const baseRpm = boost > 0 ? 2800 : 1450;
    const rpmJitter = Math.round(Math.sin(elapsed * 4.3) * 4 + Math.cos(elapsed * 7.1) * 2);
    const currentRpm = baseRpm + rpmJitter;

    // 2) Velocidad lineal de desplazamiento de la aguja en m/s
    // El carro recorre una amplitud de ~0.098 m con frecuencia angular w = (boost ? 2.1 : 0.82)
    // Mostramos la velocidad instantánea / nominal en m/s con 3 decimales
    const freq = boost > 0 ? 2.1 : 0.82;
    const nominalSpeed = boost > 0 ? 0.125 : 0.048;
    const speedWave = Math.abs(Math.cos(elapsed * freq)) * 0.014;
    const needleSpeedMs = (nominalSpeed + speedWave).toFixed(3);

    // Sombras de texto tipo cristal líquido
    ctx.shadowColor = "rgba(180, 240, 255, 0.65)";
    ctx.shadowBlur = 8;

    // Fila 1: RPM del Tambor Colector
    ctx.fillStyle = "#bfe8ff";
    ctx.font = "500 26px 'JetBrains Mono', monospace";
    ctx.fillText("TAMBOR:", 26, 122);

    ctx.fillStyle = "#ffffff";
    ctx.font = "700 44px 'JetBrains Mono', monospace";
    ctx.fillText(`${currentRpm} RPM`, 168, 125);

    // Fila 2: Velocidad de desplazamiento de la aguja (m/s)
    ctx.fillStyle = "#bfe8ff";
    ctx.font = "500 26px 'JetBrains Mono', monospace";
    ctx.fillText("AGUJA :", 26, 204);

    ctx.fillStyle = "#e6ffff";
    ctx.font = "700 44px 'JetBrains Mono', monospace";
    ctx.fillText(`${needleSpeedMs} m/s`, 168, 207);

    ctx.shadowBlur = 0;
    texture.needsUpdate = true;
  }

  // Dibujo inicial inmediato
  draw(0, 0);

  return { texture, draw };
}

// Crea y actualiza el CanvasTexture de la pantalla LCD inferior (en la fuente HV):
// Mismo fondo azul que la pantalla LCD frontal, con números ROJOS grandes simulando Voltaje (> 20.000 kV) y Amperaje (~0,5 A)
function createHvDisplayController(textures) {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 256;
  const ctx = canvas.getContext("2d");
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  textures.add(texture);

  let lastDrawTime = -1;

  function draw(elapsed, boost) {
    if (elapsed - lastDrawTime < 0.09 && lastDrawTime >= 0) return;
    lastDrawTime = elapsed;

    // Mismo fondo azul clásico de pantalla LCD retroiluminada que la pantalla frontal
    const bgGrad = ctx.createLinearGradient(0, 0, 0, 256);
    bgGrad.addColorStop(0, "#0028e8");
    bgGrad.addColorStop(0.5, "#0035ff");
    bgGrad.addColorStop(1, "#001ec4");
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, 512, 256);

    // Retícula sutil de matriz de píxeles LCD idéntica a la frontal
    ctx.fillStyle = "rgba(160, 220, 255, 0.06)";
    for (let y = 8; y < 248; y += 6) {
      ctx.fillRect(10, y, 492, 2);
    }

    // Marco interior luminoso de la pantalla
    ctx.strokeStyle = "rgba(150, 225, 255, 0.45)";
    ctx.lineWidth = 3;
    ctx.strokeRect(10, 10, 492, 236);

    // Línea divisoria central sutil entre Voltaje y Amperaje
    ctx.fillStyle = "rgba(150, 225, 255, 0.25)";
    ctx.fillRect(20, 128, 472, 2);

    // Telemetría de Alto Voltaje (> 20.000 kV) y Amperaje (~0,5 A) en letras ROJAS grandes:
    const baseKv = boost > 0 ? 24.85 : 21.45;
    const kvJitter = Math.sin(elapsed * 3.8) * 0.03 + Math.cos(elapsed * 6.3) * 0.015;
    const kvFormatted = (baseKv + kvJitter).toFixed(3);

    const baseAmp = boost > 0 ? 0.62 : 0.50;
    const ampJitter = Math.sin(elapsed * 4.6) * 0.012 + Math.cos(elapsed * 8.1) * 0.006;
    const ampFormatted = (baseAmp + ampJitter).toFixed(2).replace(".", ",");

    ctx.save();
    // Proporción condensada tipo display digital industrial para que los números de 84px llenen toda la pantalla
    ctx.scale(0.84, 1.0);

    ctx.shadowColor = "rgba(255, 20, 35, 0.75)";
    ctx.shadowBlur = 8;
    ctx.fillStyle = "#ff1f2a";
    ctx.font = "800 84px 'JetBrains Mono', monospace";

    // Fila 1 (grande): Voltaje sobre 20.000 kV en color ROJO
    ctx.fillText(`V:${kvFormatted} kV`, 28, 104);

    // Fila 2 (grande): Amperaje en rango ~0,5 A en color ROJO
    ctx.fillText(`A: ${ampFormatted} A`, 28, 220);

    ctx.restore();
    texture.needsUpdate = true;
  }

  draw(0, 0);

  return { texture, draw };
}

function createSprayConeTexture() {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext("2d");
  const grad = ctx.createLinearGradient(0, 0, 0, 256);
  grad.addColorStop(0.0, "rgba(255, 255, 255, 0.95)");
  grad.addColorStop(0.28, "rgba(195, 245, 212, 0.72)");
  grad.addColorStop(0.75, "rgba(84, 181, 60, 0.34)");
  grad.addColorStop(1.0, "rgba(84, 181, 60, 0.0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 256);

  ctx.globalCompositeOperation = "source-atop";
  for (let x = 0; x < 256; x += 4) {
    const alpha = 0.38 + 0.62 * Math.pow(Math.sin(x * 0.28) * 0.5 + 0.5, 2);
    ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
    ctx.fillRect(x, 0, 2, 256);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

// Materiales físicos calibrados para integrarse con el entorno gris de estudio fotográfico
function createCadMaterials(textures, materialsSet, lcdTexture, hvLcdTexture) {
  const drumRoughMap = createBrushedDrumRoughnessMap();
  textures.add(drumRoughMap);

  const list = [
    // 0: baseBlue (Zócalo inferior azul-acero + anillos de chumacera + dial graduado)
    new THREE.MeshPhysicalMaterial({
      color: 0xa6bad6,
      roughness: 0.32,
      metalness: 0.22,
      clearcoat: 0.4,
      clearcoatRoughness: 0.26
    }),
    // 1: aluFrame (Perfiles estructurales 30x30 de aluminio anodizado + marco exterior HV)
    new THREE.MeshStandardMaterial({
      color: 0x9aa2ac,
      roughness: 0.26,
      metalness: 0.86
    }),
    // 2: worktableDark (Mesa interior gris grafito mate que recibe sombras de los componentes)
    new THREE.MeshStandardMaterial({
      color: 0x34383d,
      roughness: 0.52,
      metalness: 0.24
    }),
    // 3: darkCharcoal (Paneles de control frontales, panel lateral gris oscuro de soporte HV, biseles LCD, tapas y carro)
    new THREE.MeshPhysicalMaterial({
      color: 0x2c3035,
      roughness: 0.36,
      metalness: 0.30,
      clearcoat: 0.24
    }),
    // 4: matteBlack (Bisagras negras, manija central, topes redondeados del módulo HV, rejillas traseras)
    new THREE.MeshPhysicalMaterial({
      color: 0x17191c,
      roughness: 0.32,
      metalness: 0.28,
      clearcoat: 0.3
    }),
    // 5: whitePanel (Fuente de poder HV interna blanca y caja trapezoidal interior)
    new THREE.MeshPhysicalMaterial({
      color: 0xe6e8ec,
      roughness: 0.28,
      metalness: 0.12,
      clearcoat: 0.65,
      clearcoatRoughness: 0.24
    }),
    // 6: silverGrey (Soporte perforado del colector, base del riel lineal, arco de cable flexible)
    new THREE.MeshStandardMaterial({
      color: 0xb2b9c2,
      roughness: 0.32,
      metalness: 0.60
    }),
    // 7: chrome (Barras guía cromadas, husillo, rejillas de ventilador, tornillería inoxidable)
    new THREE.MeshPhysicalMaterial({
      color: 0xdce6f2,
      roughness: 0.14,
      metalness: 0.94,
      clearcoat: 0.55
    }),
    // 8: chromeDrum (Rodillo colector rotatorio cromado de estudio)
    new THREE.MeshPhysicalMaterial({
      color: 0xc4d6ec,
      roughness: 0.15,
      roughnessMap: drumRoughMap,
      metalness: 0.95,
      clearcoat: 0.72,
      clearcoatRoughness: 0.08
    }),
    // 9: greenBtn (Pulsador verde frontal encendido con sombreado uniforme 3D sin quemar la cara frontal)
    new THREE.MeshPhysicalMaterial({
      color: 0x28b835,
      roughness: 0.28,
      metalness: 0.08,
      emissive: 0x168520,
      emissiveIntensity: 0.48
    }),
    // 10: redBtn (Pulsador rojo frontal + switch trasero rojo)
    new THREE.MeshPhysicalMaterial({
      color: 0xe01b1b,
      roughness: 0.22,
      metalness: 0.1,
      emissive: 0x4a0606,
      emissiveIntensity: 0.4
    }),
    // 11: crimsonRed (Aislador vertical rojo y pinza porta-electrodo roja en el carro móvil)
    new THREE.MeshPhysicalMaterial({
      color: 0xa82222,
      roughness: 0.30,
      metalness: 0.24
    }),
    // 12: cobaltBlue (Acople azul del motor paso a paso y collarín azul del carro)
    new THREE.MeshStandardMaterial({
      color: 0x264f8c,
      roughness: 0.28,
      metalness: 0.50
    }),
    // 13: lcdBlue (Pantalla LCD azul retroiluminada frontal con telemetría en vivo)
    new THREE.MeshBasicMaterial({
      map: lcdTexture,
      color: 0xffffff
    }),
    // 14: acrylicGlass (Efecto cristal transparente para puertas, cúpula y caja acrílica de la fuente HV)
    new THREE.MeshPhysicalMaterial({
      color: 0xdff2ff,
      transparent: true,
      opacity: 0.056,
      premultipliedAlpha: true,
      roughness: 0.012,
      metalness: 0.04,
      reflectivity: 0.98,
      ior: 1.50,
      clearcoat: 1.0,
      clearcoatRoughness: 0.01,
      envMapIntensity: 1.55,
      depthWrite: false,
      side: THREE.DoubleSide
    }),
    // 15: hvScreen (Pantalla LCD azul inferior con letras rojas grandes de kV y A en la fuente de poder)
    new THREE.MeshBasicMaterial({
      map: hvLcdTexture,
      color: 0xffffff
    }),
    // 16: ayaGreen (Placa verde lima detrás del calado "AYA" — verde intenso y nítido sin saturar a blanco)
    new THREE.MeshPhysicalMaterial({
      color: 0x32d420,
      roughness: 0.20,
      metalness: 0.05,
      emissive: 0x1c9410,
      emissiveIntensity: 0.68
    })
  ];

  list.forEach((m) => materialsSet.add(m));
  return list;
}

// Construye los efectos luminosos frontales y del módulo HV lateral:
// 1) Sprite de resplandor centrado en el volumen 3D del cabezal del botón verde (sin paralaje lateral)
// 2) Resplandor suave en el logo calado AYA conservando su silueta nítida (mat 16)
// 3) Pantalla LCD azul superior con datos de RPM y velocidad m/s (mat 13)
// 4) Pantalla LCD azul inferior en la fuente HV con números rojos grandes de Voltaje (>20.000 kV) y Amperaje (~0,5 A) (mat 15)
function buildFrontPanelEffects(
  staticGroup,
  featureBounds,
  materials,
  lcdController,
  hvLcdController,
  textures,
  geometries,
  materialsSet
) {
  const greenGlowTex = createGlowHaloTexture(
    "rgba(46, 215, 65, 0.52)",
    "rgba(35, 185, 50, 0.20)",
    "rgba(35, 185, 50, 0)"
  );
  textures.add(greenGlowTex);

  const blueGlowTex = createGlowHaloTexture(
    "rgba(80, 165, 255, 0.35)",
    "rgba(0, 70, 255, 0.14)",
    "rgba(0, 40, 255, 0)"
  );
  textures.add(blueGlowTex);

  let btnHaloSprite = null;
  let btnBaseSize = 0.18;
  let logoHaloMesh = null;
  let lcdGlowMesh = null;
  let hvLcdGlowMesh = null;

  // 1. Luz piloto verde encendida (mat 9) — Sprite centrado en el eje 3D del cabezal exterior del botón
  const b9 = featureBounds[9];
  if (b9 && b9.maxX > b9.minX) {
    const cx = b9.capCx !== undefined ? b9.capCx : (b9.minX + b9.maxX) * 0.5;
    const cy = b9.capCy !== undefined ? b9.capCy : (b9.minY + b9.maxY) * 0.5;
    const capMidZ = b9.maxZ - 0.018;
    const capDiam = b9.capDiam || Math.max(b9.maxX - b9.minX, b9.maxY - b9.minY);
    btnBaseSize = capDiam * 1.95;

    const spriteMat = new THREE.SpriteMaterial({
      map: greenGlowTex,
      color: 0x35e048,
      transparent: true,
      opacity: 0.34,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    materialsSet.add(spriteMat);
    btnHaloSprite = new THREE.Sprite(spriteMat);
    btnHaloSprite.position.set(cx, cy, capMidZ);
    btnHaloSprite.scale.set(btnBaseSize, btnBaseSize, 1);
    btnHaloSprite.renderOrder = 3;
    staticGroup.add(btnHaloSprite);
  }

  // 2. Resplandor verde sutil en el logo AYA calado frontal (mat 16) sin quemar el panel azul
  const b16 = featureBounds[16];
  if (b16 && b16.maxX > b16.minX) {
    const cx = (b16.minX + b16.maxX) * 0.5;
    const cy = (b16.minY + b16.maxY) * 0.5;
    const frontZ = b16.maxZ + 0.011;
    const w = (b16.maxX - b16.minX) * 1.22;
    const h = (b16.maxY - b16.minY) * 1.32;

    const logoHaloGeo = new THREE.PlaneGeometry(w, h);
    geometries.add(logoHaloGeo);
    const logoHaloMat = new THREE.MeshBasicMaterial({
      map: greenGlowTex,
      color: 0x2fd61e,
      transparent: true,
      opacity: 0.18,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    materialsSet.add(logoHaloMat);
    logoHaloMesh = new THREE.Mesh(logoHaloGeo, logoHaloMat);
    logoHaloMesh.position.set(cx, cy, frontZ);
    logoHaloMesh.renderOrder = 3;
    staticGroup.add(logoHaloMesh);
  }

  // 3. Pantalla LCD azul frontal superior con proyección nítida de RPM y m/s + halo azul suave (mat 13)
  const b13 = featureBounds[13];
  if (b13 && b13.maxX > b13.minX) {
    const cx = (b13.minX + b13.maxX) * 0.5;
    const cy = (b13.minY + b13.maxY) * 0.5;
    const cz = b13.maxZ;
    const w = b13.maxX - b13.minX;
    const h = b13.maxY - b13.minY;

    const screenGeo = new THREE.PlaneGeometry(w, h);
    geometries.add(screenGeo);
    const screenMat = new THREE.MeshBasicMaterial({
      map: lcdController.texture,
      color: 0xffffff
    });
    materialsSet.add(screenMat);
    const screenMesh = new THREE.Mesh(screenGeo, screenMat);
    screenMesh.position.set(cx, cy, cz + 0.0012);
    screenMesh.renderOrder = 2;
    staticGroup.add(screenMesh);

    const lcdGlowGeo = new THREE.PlaneGeometry(w * 1.35, h * 1.5);
    geometries.add(lcdGlowGeo);
    const lcdGlowMat = new THREE.MeshBasicMaterial({
      map: blueGlowTex,
      color: 0x3b88ff,
      transparent: true,
      opacity: 0.34,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    materialsSet.add(lcdGlowMat);
    lcdGlowMesh = new THREE.Mesh(lcdGlowGeo, lcdGlowMat);
    lcdGlowMesh.position.set(cx, cy, cz + 0.003);
    lcdGlowMesh.renderOrder = 3;
    staticGroup.add(lcdGlowMesh);
  }

  // 4. Pantalla LCD azul inferior en la fuente HV con números rojos grandes de Voltaje y Amperaje (mat 15)
  const b15 = featureBounds[15];
  if (b15 && b15.maxZ > b15.minZ) {
    const cx = b15.maxX;
    const cy = (b15.minY + b15.maxY) * 0.5;
    const cz = (b15.minZ + b15.maxZ) * 0.5;
    const w = b15.maxZ - b15.minZ;
    const h = b15.maxY - b15.minY;

    const hvScreenGeo = new THREE.PlaneGeometry(w, h);
    geometries.add(hvScreenGeo);
    const hvScreenMat = new THREE.MeshBasicMaterial({
      map: hvLcdController.texture,
      color: 0xffffff
    });
    materialsSet.add(hvScreenMat);
    const hvScreenMesh = new THREE.Mesh(hvScreenGeo, hvScreenMat);
    hvScreenMesh.position.set(cx + 0.0012, cy, cz);
    hvScreenMesh.rotation.y = Math.PI / 2;
    hvScreenMesh.renderOrder = 1;
    staticGroup.add(hvScreenMesh);

    const hvGlowGeo = new THREE.PlaneGeometry(w * 1.35, h * 1.5);
    geometries.add(hvGlowGeo);
    const hvGlowMat = new THREE.MeshBasicMaterial({
      map: blueGlowTex,
      color: 0x3b88ff,
      transparent: true,
      opacity: 0.30,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    materialsSet.add(hvGlowMat);
    hvLcdGlowMesh = new THREE.Mesh(hvGlowGeo, hvGlowMat);
    hvLcdGlowMesh.position.set(cx + 0.0025, cy, cz);
    hvLcdGlowMesh.rotation.y = Math.PI / 2;
    hvLcdGlowMesh.renderOrder = 1;
    staticGroup.add(hvLcdGlowMesh);
  }

  // 5. Tira LED de iluminación interior ("dentro de la caja") montada bajo los rieles superiores de la cabina
  const ledCurtainMeshes = [];
  const chamberPointLights = [];
  {
    // Textura de la tira SMD LED (difusor continuo + 48 chips LED brillantes a lo largo de la barra)
    const ledCanvas = document.createElement("canvas");
    ledCanvas.width = 1024;
    ledCanvas.height = 64;
    const lctx = ledCanvas.getContext("2d");

    const bgGrad = lctx.createLinearGradient(0, 0, 0, 64);
    bgGrad.addColorStop(0.0, "rgba(180, 230, 255, 0.0)");
    bgGrad.addColorStop(0.22, "rgba(205, 240, 255, 0.65)");
    bgGrad.addColorStop(0.5, "rgba(255, 255, 255, 1.0)");
    bgGrad.addColorStop(0.78, "rgba(205, 240, 255, 0.65)");
    bgGrad.addColorStop(1.0, "rgba(180, 230, 255, 0.0)");
    lctx.fillStyle = bgGrad;
    lctx.fillRect(0, 0, 1024, 64);

    // Diodos SMD individuales a lo largo de la tira LED
    const numLeds = 48;
    for (let i = 0; i < numLeds; i++) {
      const lx = ((i + 0.5) / numLeds) * 1024;
      const chipGrad = lctx.createRadialGradient(lx, 32, 1, lx, 32, 11);
      chipGrad.addColorStop(0, "rgba(255, 255, 255, 1)");
      chipGrad.addColorStop(0.55, "rgba(235, 250, 255, 0.95)");
      chipGrad.addColorStop(1, "rgba(190, 235, 255, 0)");
      lctx.fillStyle = chipGrad;
      lctx.fillRect(lx - 11, 20, 22, 24);
    }

    const ledStripTex = new THREE.CanvasTexture(ledCanvas);
    ledStripTex.colorSpace = THREE.SRGBColorSpace;
    textures.add(ledStripTex);

    // Textura del haz / cortina de luz suave que cae desde la tira LED hacia el interior de la cámara
    const curtainCanvas = document.createElement("canvas");
    curtainCanvas.width = 256;
    curtainCanvas.height = 128;
    const cctx = curtainCanvas.getContext("2d");
    const cGrad = cctx.createLinearGradient(0, 0, 0, 128);
    cGrad.addColorStop(0.0, "rgba(225, 246, 255, 0.55)");
    cGrad.addColorStop(0.32, "rgba(205, 238, 255, 0.22)");
    cGrad.addColorStop(0.75, "rgba(190, 230, 255, 0.05)");
    cGrad.addColorStop(1.0, "rgba(190, 230, 255, 0.0)");
    cctx.fillStyle = cGrad;
    cctx.fillRect(0, 0, 256, 128);
    const ledCurtainTex = new THREE.CanvasTexture(curtainCanvas);
    ledCurtainTex.colorSpace = THREE.SRGBColorSpace;
    textures.add(ledCurtainTex);

    // Coordenadas exactas del techo interior de la caja de acrílico (en unidades de escena MM_TO_SCENE)
    const ledMinX = (62 - 620) * MM_TO_SCENE;
    const ledMaxX = (1002 - 620) * MM_TO_SCENE;
    const ledCx = (ledMinX + ledMaxX) * 0.5;
    const ledLen = ledMaxX - ledMinX;
    const ledY = (581.5 - 315) * MM_TO_SCENE;
    const frontLedZ = (543.5 - 305) * MM_TO_SCENE;
    const rearLedZ = (49.5 - 305) * MM_TO_SCENE;

    const channelGeo = new THREE.BoxGeometry(ledLen, 0.015, 0.022);
    const emitterGeo = new THREE.PlaneGeometry(ledLen - 0.01, 0.020);
    const curtainGeo = new THREE.PlaneGeometry(ledLen - 0.02, 0.22);
    geometries.add(channelGeo);
    geometries.add(emitterGeo);
    geometries.add(curtainGeo);

    const channelMat = new THREE.MeshStandardMaterial({
      color: 0x22262b,
      roughness: 0.35,
      metalness: 0.75
    });
    const emitterMat = new THREE.MeshBasicMaterial({
      map: ledStripTex,
      color: 0xffffff,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    const curtainMat = new THREE.MeshBasicMaterial({
      map: ledCurtainTex,
      color: 0xdff4ff,
      transparent: true,
      opacity: 0.28,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    materialsSet.add(channelMat);
    materialsSet.add(emitterMat);
    materialsSet.add(curtainMat);

    const stripsConfig = [
      { z: frontLedZ, tiltX: 0.55, curtainZOff: -0.012, curtainTiltX: 0.18 },
      { z: rearLedZ, tiltX: -0.55, curtainZOff: 0.012, curtainTiltX: -0.18 }
    ];

    stripsConfig.forEach((cfg) => {
      // Perfil portatira LED
      const chMesh = new THREE.Mesh(channelGeo, channelMat);
      chMesh.position.set(ledCx, ledY + 0.005, cfg.z);
      staticGroup.add(chMesh);

      // Tira emisora con diodos SMD encendidos
      const emMesh = new THREE.Mesh(emitterGeo, emitterMat);
      emMesh.position.set(ledCx, ledY - 0.004, cfg.z + cfg.curtainZOff * 0.4);
      emMesh.rotation.x = Math.PI / 2 + cfg.tiltX;
      emMesh.renderOrder = 3;
      staticGroup.add(emMesh);

      // Cortina volumétrica de luz hacia el interior de la cabina
      const curMesh = new THREE.Mesh(curtainGeo, curtainMat);
      curMesh.position.set(ledCx, ledY - 0.11, cfg.z + cfg.curtainZOff);
      curMesh.rotation.x = cfg.curtainTiltX;
      curMesh.renderOrder = 3;
      staticGroup.add(curMesh);
      ledCurtainMeshes.push(curMesh);
    });

    // Luces puntuales reales dentro de la caja que iluminan el tambor colector, el carro y la mesa interior
    const lightPositionsX = [-1.15, -0.31, 0.55];
    lightPositionsX.forEach((lx) => {
      const ptLight = new THREE.PointLight(0xe8f6ff, 1.05, 3.2, 1.65);
      ptLight.position.set(lx, ledY - 0.06, -0.03);
      staticGroup.add(ptLight);
      chamberPointLights.push(ptLight);
    });
  }

  return {
    update(elapsed, boost) {
      lcdController.draw(elapsed, boost);
      hvLcdController.draw(elapsed, boost);

      const pulse = Math.sin(elapsed * 3.2) * 0.5 + 0.5;
      const boostFactor = boost > 0 ? 1.25 : 1.0;

      // Pulsación suave del indicador verde encendido
      if (materials[9]) {
        materials[9].emissiveIntensity = (0.44 + pulse * 0.16) * boostFactor;
      }
      if (btnHaloSprite) {
        btnHaloSprite.material.opacity = Math.min(0.45, (0.28 + pulse * 0.08) * boostFactor);
        const s = btnBaseSize * (0.97 + pulse * 0.06);
        btnHaloSprite.scale.set(s, s, 1);
      }

      // Resplandor sutil en el logo verde AYA manteniendo sus letras perfectamente legibles
      if (materials[16]) {
        materials[16].emissiveIntensity = (0.62 + pulse * 0.20) * boostFactor;
      }
      if (logoHaloMesh) {
        logoHaloMesh.material.opacity = Math.min(0.32, (0.14 + pulse * 0.06) * boostFactor);
        logoHaloMesh.scale.setScalar(0.98 + pulse * 0.04);
      }

      if (lcdGlowMesh) {
        lcdGlowMesh.material.opacity = 0.30 + pulse * 0.06;
      }
      if (hvLcdGlowMesh) {
        hvLcdGlowMesh.material.opacity = 0.22 + pulse * 0.05;
      }

      // Iluminación interior estable de la tira LED (con ligero incremento en modo intensificado)
      const ledIntensity = boost > 0 ? 1.32 : 1.05;
      for (let i = 0; i < chamberPointLights.length; i++) {
        chamberPointLights[i].intensity = ledIntensity;
      }
      for (let i = 0; i < ledCurtainMeshes.length; i++) {
        ledCurtainMeshes[i].material.opacity = boost > 0 ? 0.34 : 0.27;
      }
    }
  };
}

function buildElectrospinningSpray(carriageGroup, drumPivot, textures, geometries, materialsSet) {
  const tipX = (741.5 - 620) * MM_TO_SCENE;
  const tipY = (348.5 - 315) * MM_TO_SCENE;
  const holderZ = (282.0 - 305) * MM_TO_SCENE;
  const tipZ = (289.5 - 305) * MM_TO_SCENE;

  const targetX = tipX;
  const targetY = (342.4 - 315) * MM_TO_SCENE;
  const targetZ = (341.6 - 305) * MM_TO_SCENE;

  const sprayRoot = new THREE.Group();
  sprayRoot.renderOrder = 1;
  carriageGroup.add(sprayRoot);

  const needleLen = tipZ - holderZ;
  const needleGeo = new THREE.CylinderGeometry(0.45 * MM_TO_SCENE, 1.1 * MM_TO_SCENE, needleLen, 12);
  needleGeo.rotateX(Math.PI / 2);
  geometries.add(needleGeo);
  const needleMat = new THREE.MeshStandardMaterial({ color: 0xe2ebf5, metalness: 0.95, roughness: 0.12 });
  materialsSet.add(needleMat);
  const needleMesh = new THREE.Mesh(needleGeo, needleMat);
  needleMesh.position.set(tipX, tipY, (holderZ + tipZ) * 0.5);
  sprayRoot.add(needleMesh);

  const taylorGeo = new THREE.ConeGeometry(1.4 * MM_TO_SCENE, 3.8 * MM_TO_SCENE, 16);
  taylorGeo.rotateX(Math.PI / 2);
  geometries.add(taylorGeo);
  const taylorMat = new THREE.MeshBasicMaterial({
    color: 0x9effbc,
    transparent: true,
    opacity: 0.95
  });
  materialsSet.add(taylorMat);
  const taylorMesh = new THREE.Mesh(taylorGeo, taylorMat);
  taylorMesh.position.set(tipX, tipY, tipZ + 1.4 * MM_TO_SCENE);
  sprayRoot.add(taylorMesh);

  const startVec = new THREE.Vector3(tipX, tipY, tipZ);
  const endVec = new THREE.Vector3(targetX, targetY, targetZ);
  const jetVec = new THREE.Vector3().subVectors(endVec, startVec);
  const jetLen = jetVec.length();
  const midVec = new THREE.Vector3().addVectors(startVec, endVec).multiplyScalar(0.5);

  const coneTex = createSprayConeTexture();
  textures.add(coneTex);

  const outerConeRadius = 21 * MM_TO_SCENE;
  const outerConeGeo = new THREE.CylinderGeometry(0.6 * MM_TO_SCENE, outerConeRadius, jetLen, 28, 1, true);
  geometries.add(outerConeGeo);
  const outerConeMat = new THREE.MeshBasicMaterial({
    map: coneTex,
    color: 0xbfffe0,
    transparent: true,
    opacity: 0.46,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide
  });
  materialsSet.add(outerConeMat);
  const outerCone = new THREE.Mesh(outerConeGeo, outerConeMat);
  outerCone.position.copy(midVec);
  outerCone.quaternion.setFromUnitVectors(new THREE.Vector3(0, -1, 0), jetVec.clone().normalize());
  sprayRoot.add(outerCone);

  const innerConeRadius = 11.5 * MM_TO_SCENE;
  const innerConeGeo = new THREE.CylinderGeometry(0.35 * MM_TO_SCENE, innerConeRadius, jetLen, 24, 1, true);
  geometries.add(innerConeGeo);
  const innerConeMat = new THREE.MeshBasicMaterial({
    map: coneTex,
    color: 0xffffff,
    transparent: true,
    opacity: 0.72,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide
  });
  materialsSet.add(innerConeMat);
  const innerCone = new THREE.Mesh(innerConeGeo, innerConeMat);
  innerCone.position.copy(midVec);
  innerCone.quaternion.copy(outerCone.quaternion);
  sprayRoot.add(innerCone);

  const numFilaments = 3;
  const segsPerFilament = 44;
  const filaments = [];
  for (let f = 0; f < numFilaments; f++) {
    const fPos = new Float32Array((segsPerFilament + 1) * 3);
    const fGeo = new THREE.BufferGeometry();
    fGeo.setAttribute("position", new THREE.BufferAttribute(fPos, 3));
    geometries.add(fGeo);
    const fMat = new THREE.LineBasicMaterial({
      color: f === 0 ? 0xffffff : 0x7cf59a,
      transparent: true,
      opacity: f === 0 ? 0.9 : 0.68,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    materialsSet.add(fMat);
    const line = new THREE.Line(fGeo, fMat);
    sprayRoot.add(line);
    filaments.push({ fGeo, fPos, phaseOffset: (f * Math.PI * 2) / numFilaments });
  }

  const PARTICLE_COUNT = 340;
  const pPositions = new Float32Array(PARTICLE_COUNT * 3);
  const pSeeds = new Float32Array(PARTICLE_COUNT * 4);
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    pSeeds[i * 4] = Math.random();
    pSeeds[i * 4 + 1] = Math.random() * Math.PI * 2;
    pSeeds[i * 4 + 2] = Math.sqrt(Math.random());
    pSeeds[i * 4 + 3] = 0.75 + Math.random() * 0.5;
  }
  const pGeo = new THREE.BufferGeometry();
  pGeo.setAttribute("position", new THREE.BufferAttribute(pPositions, 3));
  geometries.add(pGeo);
  const pTex = createSprayParticleTexture();
  textures.add(pTex);
  const pMat = new THREE.PointsMaterial({
    map: pTex,
    size: 0.044,
    transparent: true,
    opacity: 0.92,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });
  materialsSet.add(pMat);
  const sprayParticles = new THREE.Points(pGeo, pMat);
  sprayRoot.add(sprayParticles);

  const impactGeo = new THREE.CircleGeometry(outerConeRadius * 1.15, 32);
  geometries.add(impactGeo);
  const impactMat = new THREE.MeshBasicMaterial({
    map: pTex,
    color: 0xc8ffde,
    transparent: true,
    opacity: 0.78,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide
  });
  materialsSet.add(impactMat);
  const impactHalo = new THREE.Mesh(impactGeo, impactMat);
  impactHalo.position.set(targetX, targetY, targetZ - 0.5 * MM_TO_SCENE);
  sprayRoot.add(impactHalo);

  const webMatRadius = 59.4 * MM_TO_SCENE;
  const webMatLength = 215 * MM_TO_SCENE;
  const webCylGeo = new THREE.CylinderGeometry(webMatRadius, webMatRadius, webMatLength, 48, 1, true);
  webCylGeo.rotateZ(Math.PI / 2);
  geometries.add(webCylGeo);
  const webCylMat = new THREE.MeshPhysicalMaterial({
    color: 0xeef7f0,
    transparent: true,
    opacity: 0.24,
    roughness: 0.32,
    metalness: 0.12,
    clearcoat: 0.6,
    depthWrite: false
  });
  materialsSet.add(webCylMat);
  const webCylMesh = new THREE.Mesh(webCylGeo, webCylMat);
  webCylMesh.position.set((770.8 - 620) * MM_TO_SCENE, 0, 0);
  drumPivot.add(webCylMesh);

  return {
    update(elapsed, boost) {
      const rate = boost > 0 ? 3.2 : 1.65;
      const intensity = boost > 0 ? 1.35 : 1.0;

      coneTex.offset.x = (elapsed * 1.4 * rate) % 1;
      outerCone.rotation.y = elapsed * 4.0;
      innerCone.rotation.y = -elapsed * 6.5;
      outerConeMat.opacity = Math.min(0.72, 0.42 * intensity + Math.sin(elapsed * 18) * 0.04);
      innerConeMat.opacity = Math.min(0.90, 0.65 * intensity + Math.cos(elapsed * 23) * 0.06);
      impactMat.opacity = Math.min(0.95, 0.70 * intensity + Math.sin(elapsed * 20) * 0.08);
      impactHalo.scale.setScalar(0.94 + Math.sin(elapsed * 16) * 0.08);

      for (let f = 0; f < numFilaments; f++) {
        const { fGeo, fPos, phaseOffset } = filaments[f];
        for (let s = 0; s <= segsPerFilament; s++) {
          const u = s / segsPerFilament;
          const env = u < 0.12 ? 0 : Math.pow((u - 0.12) / 0.88, 1.25) * outerConeRadius * 0.88;
          const theta = u * 22.0 - elapsed * 24.0 * rate + phaseOffset;
          const lx = startVec.x + (endVec.x - startVec.x) * u + Math.cos(theta) * env;
          const ly = startVec.y + (endVec.y - startVec.y) * u + Math.sin(theta) * env;
          const lz = startVec.z + (endVec.z - startVec.z) * u;
          fPos[s * 3] = lx;
          fPos[s * 3 + 1] = ly;
          fPos[s * 3 + 2] = lz;
        }
        fGeo.attributes.position.needsUpdate = true;
      }

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const t0 = pSeeds[i * 4];
        const baseAng = pSeeds[i * 4 + 1];
        const radFactor = pSeeds[i * 4 + 2];
        const spd = pSeeds[i * 4 + 3];

        const u = (t0 + elapsed * rate * spd) % 1.0;
        const coneR = Math.pow(u, 1.15) * outerConeRadius * radFactor * (boost > 0 ? 1.15 : 1.0);
        const swirl = baseAng + u * 6.5 - elapsed * 8.0;

        const px = startVec.x + (endVec.x - startVec.x) * u + Math.cos(swirl) * coneR;
        let py = startVec.y + (endVec.y - startVec.y) * u + Math.sin(swirl) * coneR;
        const pz = startVec.z + (endVec.z - startVec.z) * u;

        if (u > 0.88) {
          const hit = (u - 0.88) / 0.12;
          py += hit * 3.5 * MM_TO_SCENE;
        }

        pPositions[i * 3] = px;
        pPositions[i * 3 + 1] = py;
        pPositions[i * 3 + 2] = pz;
      }
      pGeo.attributes.position.needsUpdate = true;
    }
  };
}

const CAD_BIN_URL = "/aya-machine.bin?v=6";

function fetchCadMachineBuffer() {
  if (
    typeof window !== "undefined" &&
    window.__AYA_MACHINE_PROMISE__ &&
    window.__AYA_MACHINE_URL__ === CAD_BIN_URL
  ) {
    return window.__AYA_MACHINE_PROMISE__;
  }
  const p = fetch(CAD_BIN_URL).then((res) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.arrayBuffer();
  });
  if (typeof window !== "undefined") {
    window.__AYA_MACHINE_URL__ = CAD_BIN_URL;
    window.__AYA_MACHINE_PROMISE__ = p;
  }
  return p;
}

async function loadCadMachineModel(
  group,
  materials,
  lcdController,
  hvLcdController,
  textures,
  geometries,
  materialsSet
) {
  const arrayBuf = await fetchCadMachineBuffer();
  const view = new DataView(arrayBuf);

  const magic = String.fromCharCode(
    view.getUint8(0),
    view.getUint8(1),
    view.getUint8(2),
    view.getUint8(3)
  );
  if (magic !== "AYA3") throw new Error("Formato binario CAD inválido");

  const numMeshes = view.getUint32(4, true);
  let offset = 8;

  const staticGroup = new THREE.Group();
  const drumPivot = new THREE.Group();
  drumPivot.position.set(0, DRUM_AXIS_Y, DRUM_AXIS_Z);
  const carriageGroup = new THREE.Group();
  const fan1Pivot = new THREE.Group();
  fan1Pivot.position.set(0, FAN1_AXIS_Y, FAN1_AXIS_Z);
  const fan2Pivot = new THREE.Group();
  fan2Pivot.position.set(0, FAN2_AXIS_Y, FAN2_AXIS_Z);

  group.add(staticGroup);
  group.add(drumPivot);
  group.add(carriageGroup);
  group.add(fan1Pivot);
  group.add(fan2Pivot);

  // Guardaremos los bounding boxes exactos de:
  // mat 9 (pulsador/luz verde frontal), mat 13 (pantalla LCD azul superior),
  // mat 15 (pantalla LCD azul inferior de la fuente HV), mat 16 (logo AYA verde)
  const featureBounds = {};

  for (let m = 0; m < numMeshes; m++) {
    const matId = view.getUint16(offset, true);
    const anim = view.getUint16(offset + 2, true);
    const nv = view.getUint32(offset + 4, true);
    const ni = view.getUint32(offset + 8, true);
    offset += 12;

    const posBytes = nv * 6;
    const qPos = new Int16Array(arrayBuf, offset, nv * 3);
    offset += posBytes;

    const normBytes = nv * 3;
    const qNorm = new Int8Array(arrayBuf, offset, nv * 3);
    offset += normBytes;

    const padBytes = (4 - ((posBytes + normBytes) % 4)) % 4;
    offset += padBytes;

    const idxBytes = ni * 4;
    const indices = new Uint32Array(arrayBuf, offset, ni);
    offset += idxBytes;

    const positions = new Float32Array(nv * 3);
    const normals = new Float32Array(nv * 3);
    const uvs = matId === 8 || matId === 13 || matId === 15 ? new Float32Array(nv * 2) : null;

    const offY =
      anim === 1 ? DRUM_AXIS_Y : anim === 3 ? FAN1_AXIS_Y : anim === 4 ? FAN2_AXIS_Y : 0;
    const offZ =
      anim === 1 ? DRUM_AXIS_Z : anim === 3 ? FAN1_AXIS_Z : anim === 4 ? FAN2_AXIS_Z : 0;

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    for (let v = 0; v < nv; v++) {
      const px = qPos[v * 3] * QPOS_TO_SCENE;
      const py = qPos[v * 3 + 1] * QPOS_TO_SCENE - offY;
      const pz = qPos[v * 3 + 2] * QPOS_TO_SCENE - offZ;
      positions[v * 3] = px;
      positions[v * 3 + 1] = py;
      positions[v * 3 + 2] = pz;

      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
      if (pz < minZ) minZ = pz;
      if (pz > maxZ) maxZ = pz;

      normals[v * 3] = qNorm[v * 3] / 127;
      normals[v * 3 + 1] = qNorm[v * 3 + 1] / 127;
      normals[v * 3 + 2] = qNorm[v * 3 + 2] / 127;

      if (matId === 8 && uvs) {
        const angle = Math.atan2(py, pz) / (Math.PI * 2) + 0.5;
        uvs[v * 2] = px * 2.5;
        uvs[v * 2 + 1] = angle * 6;
      }
    }

    if (matId === 13 && uvs) {
      const spanX = Math.max(1e-5, maxX - minX);
      const spanY = Math.max(1e-5, maxY - minY);
      for (let v = 0; v < nv; v++) {
        uvs[v * 2] = (positions[v * 3] - minX) / spanX;
        uvs[v * 2 + 1] = (positions[v * 3 + 1] - minY) / spanY;
      }
    }

    if (matId === 15 && uvs) {
      const spanZ = Math.max(1e-5, maxZ - minZ);
      const spanY = Math.max(1e-5, maxY - minY);
      for (let v = 0; v < nv; v++) {
        // Mirando hacia -X (vista Módulo HV), la izquierda es maxZ y la derecha es minZ
        uvs[v * 2] = (maxZ - positions[v * 3 + 2]) / spanZ;
        uvs[v * 2 + 1] = (positions[v * 3 + 1] - minY) / spanY;
      }
    }

    if (matId === 9 || matId === 13 || matId === 15 || matId === 16) {
      featureBounds[matId] = { minX, maxX, minY, maxY, minZ, maxZ };
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
    if (uvs) geo.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
    geo.setIndex(new THREE.BufferAttribute(new Uint32Array(indices), 1));
    geometries.add(geo);

    const meshObj = new THREE.Mesh(geo, materials[matId] || materials[7]);
    if (matId === 14) {
      meshObj.renderOrder = 2;
    } else if (matId === 15) {
      meshObj.renderOrder = 1;
    } else if (matId !== 9 && matId !== 13 && matId !== 16) {
      meshObj.castShadow = true;
      meshObj.receiveShadow = true;
    }

    if (anim === 1) {
      drumPivot.add(meshObj);
    } else if (anim === 2) {
      carriageGroup.add(meshObj);
    } else if (anim === 3) {
      fan1Pivot.add(meshObj);
    } else if (anim === 4) {
      fan2Pivot.add(meshObj);
    } else {
      staticGroup.add(meshObj);
    }
  }

  const sprayEffect = buildElectrospinningSpray(
    carriageGroup,
    drumPivot,
    textures,
    geometries,
    materialsSet
  );

  const frontEffects = buildFrontPanelEffects(
    staticGroup,
    featureBounds,
    materials,
    lcdController,
    hvLcdController,
    textures,
    geometries,
    materialsSet
  );

  sprayEffect.update(0.35, 0);
  frontEffects.update(0.35, 0);

  let carriagePhase = 0.35 * 0.82;
  let lastRigElapsed = 0.35;
  let smoothThrottle = 0;

  return {
    drumPivot,
    carriageGroup,
    fan1Pivot,
    fan2Pivot,
    update(elapsed, boost) {
      const dt = Math.max(0, Math.min(0.05, elapsed - lastRigElapsed));
      lastRigElapsed = elapsed;
      const targetThrottle = boost > 0 ? 1 : 0;
      smoothThrottle += (targetThrottle - smoothThrottle) * Math.min(1, dt * 4.5);

      const speed = 1.5 + smoothThrottle * 3.0;
      drumPivot.rotation.x += 0.025 * speed;
      fan1Pivot.rotation.x += 0.22 * speed;
      fan2Pivot.rotation.x += 0.22 * speed;

      const freq = 0.82 + smoothThrottle * (2.1 - 0.82);
      carriagePhase += dt * freq;
      carriageGroup.position.x = Math.sin(carriagePhase) * 0.35;

      sprayEffect.update(elapsed, boost);
      frontEffects.update(elapsed, boost);
    }
  };
}

export default function Hero() {
  const heroRef = useRef(null);
  const visualRef = useRef(null);
  const mountRef = useRef(null);
  const rpmValRef = useRef(null);
  const rpmArcRef = useRef(null);
  const velValRef = useRef(null);
  const velArcRef = useRef(null);
  const telemetryHudRef = useRef(null);
  const boostFillRef = useRef(null);

  const controlRef = useRef({
    camMode: "orbit",
    boostTime: 0,
    dragOffsetAzimuth: 0,
    dragOffsetPolar: 0
  });

  const [camMode, setCamMode] = useState("orbit");
  const [isBoosting, setIsBoosting] = useState(false);
  const [loadingCad, setLoadingCad] = useState(true);
  const [responseText, setResponseText] = useState("");
  const [metricsText, setMetricsText] = useState("");

  useEffect(() => {
    const heroEl = heroRef.current;
    const hostEl = visualRef.current;
    const mountEl = mountRef.current;
    if (!heroEl || !hostEl || !mountEl) return;

    const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let paused = reducedQuery.matches;
    let visible = true;
    let disposed = false;
    let rafId = 0;
    let elapsed = 0;
    let last = performance.now();

    const pointer = new THREE.Vector2(0, 0);
    const smooth = new THREE.Vector2(0, 0);
    let isDragging = false;
    let lastDrag = { x: 0, y: 0 };

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: "high-performance"
      });
    } catch {
      setLoadingCad(false);
      setMetricsText("WebGL no disponible. Se conserva la composición estática.");
      return;
    }

    renderer.setPixelRatio(
      Math.min(window.devicePixelRatio || 1, window.innerWidth < 700 ? 1.25 : 1.75)
    );
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.78;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setClearColor(0x000000, 0);
    mountEl.innerHTML = "";
    mountEl.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    const currentCamPos = new THREE.Vector3(-2.95, 2.22, 5.55);
    const currentLookAt = new THREE.Vector3(0.02, -0.02, 0);
    camera.position.copy(currentCamPos);

    const geometries = new Set();
    const materialsSet = new Set();
    const textures = new Set();

    const pmrem = new THREE.PMREMGenerator(renderer);
    const room = new RoomEnvironment();
    room.background = new THREE.Color(0x30353c);
    room.traverse((obj) => {
      if (obj.isMesh && obj.material && obj.material.color) {
        if (obj.scale.x < 0 || obj.geometry?.type === "BoxGeometry") {
          if (!obj.material.emissive || obj.material.emissiveIntensity === 0) {
            obj.material.color.setHex(0x383e47);
          }
        }
      }
    });
    const envTarget = pmrem.fromScene(room, 0.032);
    scene.environment = envTarget.texture;
    scene.environmentIntensity = 0.62;
    room.dispose();

    scene.add(new THREE.HemisphereLight(0xf2f5fa, 0x2c3138, 0.68));

    const key = new THREE.DirectionalLight(0xfff1de, 2.4);
    key.position.set(-3.4, 5.6, 4.8);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1.5;
    key.shadow.camera.far = 14.0;
    key.shadow.camera.left = -3.2;
    key.shadow.camera.right = 3.2;
    key.shadow.camera.top = 2.5;
    key.shadow.camera.bottom = -2.5;
    key.shadow.bias = -0.0005;
    key.shadow.normalBias = 0.014;
    key.shadow.radius = 3.2;
    scene.add(key);

    const fill = new THREE.DirectionalLight(0xcce2ff, 1.15);
    fill.position.set(4.5, 1.8, 2.5);
    scene.add(fill);

    const rim = new THREE.DirectionalLight(0xdfffe8, 2.6);
    rim.position.set(0.5, 3.4, -3.8);
    scene.add(rim);

    const group = new THREE.Group();
    group.scale.setScalar(0.92);
    group.position.y = 0.06;
    scene.add(group);

    const lcdController = createLcdDisplayController(textures);
    const hvLcdController = createHvDisplayController(textures);
    const materials = createCadMaterials(
      textures,
      materialsSet,
      lcdController.texture,
      hvLcdController.texture
    );

    const floorY = -1.035 * 0.92 + 0.06;
    const shadowFloorGeo = new THREE.PlaneGeometry(24.0, 24.0);
    geometries.add(shadowFloorGeo);
    const shadowFloorMat = new THREE.ShadowMaterial({
      opacity: 0.34,
      color: 0x111418
    });
    materialsSet.add(shadowFloorMat);
    const shadowFloor = new THREE.Mesh(shadowFloorGeo, shadowFloorMat);
    shadowFloor.position.set(0, floorY, 0);
    shadowFloor.rotation.x = -Math.PI / 2;
    shadowFloor.receiveShadow = true;
    scene.add(shadowFloor);

    const pedestalCanvas = document.createElement("canvas");
    pedestalCanvas.width = 512;
    pedestalCanvas.height = 512;
    const pctx = pedestalCanvas.getContext("2d");
    const outerGlow = pctx.createRadialGradient(256, 256, 24, 256, 256, 215);
    outerGlow.addColorStop(0, "rgba(18, 21, 25, 0.48)");
    outerGlow.addColorStop(0.38, "rgba(22, 26, 31, 0.22)");
    outerGlow.addColorStop(0.72, "rgba(110, 130, 145, 0.05)");
    outerGlow.addColorStop(1, "rgba(40, 44, 49, 0)");
    pctx.fillStyle = outerGlow;
    pctx.fillRect(0, 0, 512, 512);

    const pedestalTex = new THREE.CanvasTexture(pedestalCanvas);
    textures.add(pedestalTex);
    const pedestalMat = new THREE.MeshBasicMaterial({
      map: pedestalTex,
      transparent: true,
      depthWrite: false
    });
    materialsSet.add(pedestalMat);
    const pedestalGeo = new THREE.PlaneGeometry(8.2, 5.4);
    geometries.add(pedestalGeo);
    const pedestalPlane = new THREE.Mesh(pedestalGeo, pedestalMat);
    pedestalPlane.position.set(0.0, floorY - 0.005, 0.0);
    pedestalPlane.rotation.x = -Math.PI / 2;
    scene.add(pedestalPlane);

    let rig = null;
    loadCadMachineModel(
      group,
      materials,
      lcdController,
      hvLcdController,
      textures,
      geometries,
      materialsSet
    )
      .then((loadedRig) => {
        if (disposed) return;
        rig = loadedRig;
        setLoadingCad(false);
        heroEl.classList.add("webgl-ready");
      })
      .catch((err) => {
        if (disposed) return;
        console.error(err);
        setLoadingCad(false);
        setResponseText("Error al cargar el modelo binario CAD.");
      });

    const onPointerDown = (e) => {
      if (e.target.closest("button, a")) return;
      isDragging = true;
      lastDrag = { x: e.clientX, y: e.clientY };
    };

    const onPointerUp = () => {
      isDragging = false;
    };

    const onPointerMove = (e) => {
      const r = hostEl.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        pointer.set(
          THREE.MathUtils.clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1, 1),
          THREE.MathUtils.clamp(1 - ((e.clientY - r.top) / r.height) * 2, -1, 1)
        );
      }
      if (isDragging) {
        const dx = (e.clientX - lastDrag.x) * 0.008;
        const dy = (e.clientY - lastDrag.y) * 0.006;
        controlRef.current.dragOffsetAzimuth -= dx;
        controlRef.current.dragOffsetPolar = THREE.MathUtils.clamp(
          controlRef.current.dragOffsetPolar + dy,
          -0.45,
          0.55
        );
        lastDrag = { x: e.clientX, y: e.clientY };
      }
    };

    const onPointerLeave = () => {
      if (!isDragging) pointer.set(0, 0);
    };

    const onKeyDown = (e) => {
      const step = 0.14;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        controlRef.current.dragOffsetAzimuth += step;
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        controlRef.current.dragOffsetAzimuth -= step;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        controlRef.current.dragOffsetPolar = THREE.MathUtils.clamp(
          controlRef.current.dragOffsetPolar + step * 0.6,
          -0.45,
          0.55
        );
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        controlRef.current.dragOffsetPolar = THREE.MathUtils.clamp(
          controlRef.current.dragOffsetPolar - step * 0.6,
          -0.45,
          0.55
        );
      }
    };

    hostEl.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);
    hostEl.addEventListener("pointermove", onPointerMove);
    hostEl.addEventListener("pointerleave", onPointerLeave);
    hostEl.addEventListener("keydown", onKeyDown);

    const resize = () => {
      const r = mountEl.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      renderer.setSize(r.width, r.height);
      camera.aspect = r.width / r.height;
      if (window.innerWidth > 980) {
        // Desplazamos el centro óptico hacia la derecha (68% del ancho) manteniendo el canvas al 100% sin bordes de recorte
        camera.setViewOffset(r.width, r.height, -r.width * 0.18, 16, r.width, r.height);
      } else {
        camera.clearViewOffset();
      }
      camera.updateProjectionMatrix();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(mountEl);
    resize();

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]) {
          visible = entries[0].isIntersecting;
        }
      },
      { threshold: 0.05 }
    );
    io.observe(heroEl);

    const onMotionChange = (e) => {
      paused = e.matches;
    };
    reducedQuery.addEventListener?.("change", onMotionChange);

    const targetPos = new THREE.Vector3();
    const targetLook = new THREE.Vector3(0.02, -0.02, 0);
    let hudThrottle = 0;
    let lastHudTextTime = -1;
    let wasBoosting = false;
    const ARC_MAX_LEN = 58.64; // 240 deg arc for r=14

    const animate = (now = performance.now()) => {
      if (disposed) return;
      rafId = requestAnimationFrame(animate);
      if (!visible) {
        last = now;
        return;
      }

      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      if (!paused) elapsed += dt;

      if (controlRef.current.boostTime > 0) {
        controlRef.current.boostTime = Math.max(0, controlRef.current.boostTime - dt);
      }

      const boostActive = controlRef.current.boostTime > 0;
      if (boostActive !== wasBoosting) {
        wasBoosting = boostActive;
        setIsBoosting(boostActive);
        if (!boostActive) {
          setResponseText("");
        }
      }

      const damping = 1 - Math.exp(-dt * 5.5);
      smooth.lerp(pointer, damping);

      // Telemetría en vivo estilo SpaceX (arcos SVG a 60fps + dígitos tabulares a ~12Hz)
      const targetHudThrottle = boostActive ? 1 : 0;
      hudThrottle += (targetHudThrottle - hudThrottle) * Math.min(1, dt * 4.5);

      const baseRpm = 1450 + hudThrottle * (2800 - 1450);
      const rpmJitter = Math.sin(elapsed * 4.3) * 4 + Math.cos(elapsed * 7.1) * 2;
      const liveRpm = Math.round(baseRpm + rpmJitter);

      const freq = 0.82 + hudThrottle * (2.1 - 0.82);
      const baseVel = 0.048 + hudThrottle * (0.125 - 0.048);
      const velWave = Math.abs(Math.cos(elapsed * freq)) * (0.011 + hudThrottle * 0.008);
      const liveVel = baseVel + velWave;

      if (rpmArcRef.current) {
        const rpmRatio = THREE.MathUtils.clamp(liveRpm / 3100, 0.08, 0.98);
        rpmArcRef.current.style.strokeDashoffset = `${(ARC_MAX_LEN * (1 - rpmRatio)).toFixed(2)}`;
      }
      if (velArcRef.current) {
        const velRatio = THREE.MathUtils.clamp(liveVel / 0.155, 0.08, 0.98);
        velArcRef.current.style.strokeDashoffset = `${(ARC_MAX_LEN * (1 - velRatio)).toFixed(2)}`;
      }
      if (boostFillRef.current) {
        const pct = boostActive ? (controlRef.current.boostTime / 5.0) * 100 : 0;
        boostFillRef.current.style.width = `${pct.toFixed(1)}%`;
      }

      if (elapsed - lastHudTextTime > 0.08 || lastHudTextTime < 0) {
        lastHudTextTime = elapsed;
        if (rpmValRef.current) {
          rpmValRef.current.textContent = String(liveRpm);
        }
        if (velValRef.current) {
          velValRef.current.textContent = liveVel.toFixed(3);
        }
      }

      if (rig && !paused) {
        rig.update(elapsed, controlRef.current.boostTime);
      }

      const activeMode = controlRef.current.camMode;
      const azOffset = controlRef.current.dragOffsetAzimuth;
      const polOffset = controlRef.current.dragOffsetPolar;

      if (activeMode === "orbit") {
        const baseAngle = -0.48 + elapsed * 0.24 + azOffset + smooth.x * 0.45;
        const radius = 6.25;
        const height =
          1.70 + Math.sin(elapsed * 0.42) * 0.36 + polOffset * 2.2 - smooth.y * 0.55;
        targetPos.set(
          Math.sin(baseAngle) * radius,
          height,
          Math.cos(baseAngle) * radius
        );
        targetLook.set(0.02, -0.02, 0);
      } else {
        const preset = CAMERA_PRESETS[activeMode] || CAMERA_PRESETS.isoFront;
        targetPos.set(...preset.pos);
        targetLook.set(...preset.look);
        targetPos.x += smooth.x * 0.22 + azOffset * 1.5;
        targetPos.y += smooth.y * 0.16 + polOffset * 1.1;
      }

      currentCamPos.lerp(targetPos, damping);
      currentLookAt.lerp(targetLook, damping);
      camera.position.copy(currentCamPos);
      camera.lookAt(currentLookAt);

      renderer.render(scene, camera);
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);
      ro.disconnect();
      io.disconnect();
      reducedQuery.removeEventListener?.("change", onMotionChange);
      hostEl.removeEventListener("pointerdown", onPointerDown);
      window.addEventListener("pointerup", onPointerUp);
      hostEl.removeEventListener("pointermove", onPointerMove);
      hostEl.removeEventListener("pointerleave", onPointerLeave);
      hostEl.removeEventListener("keydown", onKeyDown);

      geometries.forEach((g) => g.dispose());
      materialsSet.forEach((m) => m.dispose());
      textures.forEach((t) => t.dispose());
      envTarget.dispose();
      pmrem.dispose();
      renderer.dispose();
    };
  }, []);

  const handleSelectCamera = (modeId) => {
    controlRef.current.camMode = modeId;
    controlRef.current.dragOffsetAzimuth = 0;
    controlRef.current.dragOffsetPolar = 0;
    setCamMode(modeId);
    setResponseText(CAMERA_LABELS[modeId] || "");
  };

  const handleBoost = () => {
    if (controlRef.current.boostTime > 0.4) {
      controlRef.current.boostTime = 0;
      setIsBoosting(false);
      setResponseText("Régimen nominal restablecido: 1450 RPM · 0.048 m/s.");
    } else {
      controlRef.current.boostTime = 5.0;
      setIsBoosting(true);
      setResponseText(
        "Impulso HV activo (22.8 kV): Colector a 2800 RPM · Aguja a 0.125 m/s."
      );
    }
  };

  return (
    <section
      ref={heroRef}
      className={`hero ${styles.hero}`}
      aria-label="Aya Technologies"
    >
      {/* ── CONTENIDO EDITORIAL IZQUIERDO ── */}
      <div className={styles.heroContent}>
        <h1 className={styles.title}>
          Ciencia que se fabrica.
          <br />
          <em>Ingeniería que se adapta.</em>
        </h1>

        <p className={styles.bodycopy}>
          Si no existe, lo creamos. Si ya lo tienes, lo mejoramos.
        </p>

        <div className={styles.actionsRow}>
          <Link to="/contacto" className={styles.btnPrimary}>
            Conversemos
          </Link>
        </div>

        <p id="response" className={styles.response} aria-live="polite">
          {responseText}
        </p>

        <div className={styles.portalInline}>
          <span>¿Tienes un proyecto en curso?</span>
          <Link to="/login" className={styles.portalLink}>
            Seguimiento de proyecto →
          </Link>
        </div>
      </div>

      {/* ── ESCENARIO 3D INTEGRADO AL CICLORAMA DE ESTUDIO (#visual) ── */}
      <div
        id="visual"
        ref={visualRef}
        className={`visual ${styles.visual}`}
        tabIndex={0}
        aria-label="Modelo 3D CAD interactivo del equipo de electrohilado AYA"
      >
        <div ref={mountRef} className={`canvas-mount ${styles.canvasMount}`} />

        {loadingCad && (
          <div className={styles.fallbackArt} aria-hidden="true">
            AYA
          </div>
        )}
      </div>

      {/* ── DOCK INFERIOR DE TELEMETRÍA AEROESPACIAL + CONTROL DE AGUJA + VISTAS ── */}
      <div className={styles.sceneCaption}>
        {/* Cápsula translúcida de telemetría en vivo + botón de impulso de electrohilado */}
        <div
          ref={telemetryHudRef}
          className={styles.telemetryHud}
          data-boost={isBoosting}
          aria-label="Telemetría en vivo y control del cabezal de electrohilado"
        >
          {/* Medidor circular 1: RPM del Tambor Colector */}
          <div className={styles.telemetryMetric}>
            <div className={styles.gaugeWrap} aria-hidden="true">
              <svg className={styles.gaugeSvg} viewBox="0 0 36 36">
                <circle
                  className={styles.gaugeTrack}
                  cx="18"
                  cy="18"
                  r="14"
                  strokeDasharray="58.64 88"
                  transform="rotate(150 18 18)"
                />
                <circle
                  ref={rpmArcRef}
                  className={styles.gaugeArc}
                  cx="18"
                  cy="18"
                  r="14"
                  strokeDasharray="58.64 88"
                  strokeDashoffset="31.08"
                  transform="rotate(150 18 18)"
                />
              </svg>
              <span className={styles.gaugeCoreDot} />
            </div>
            <div className={styles.metricData}>
              <span className={styles.metricLabel}>COLECTOR</span>
              <div className={styles.metricValueRow}>
                <span ref={rpmValRef} className={styles.metricNumber}>
                  1450
                </span>
                <span className={styles.metricUnit}>RPM</span>
              </div>
            </div>
          </div>

          <span className={styles.telemetryDivider} aria-hidden="true" />

          {/* Medidor circular 2: Velocidad lineal de la aguja */}
          <div className={styles.telemetryMetric}>
            <div className={styles.gaugeWrap} aria-hidden="true">
              <svg className={styles.gaugeSvg} viewBox="0 0 36 36">
                <circle
                  className={styles.gaugeTrack}
                  cx="18"
                  cy="18"
                  r="14"
                  strokeDasharray="58.64 88"
                  transform="rotate(150 18 18)"
                />
                <circle
                  ref={velArcRef}
                  className={styles.gaugeArc}
                  cx="18"
                  cy="18"
                  r="14"
                  strokeDasharray="58.64 88"
                  strokeDashoffset="38.20"
                  transform="rotate(150 18 18)"
                />
              </svg>
              <span className={styles.gaugeCoreDot} />
            </div>
            <div className={styles.metricData}>
              <span className={styles.metricLabel}>VEL. AGUJA</span>
              <div className={styles.metricValueRow}>
                <span ref={velValRef} className={styles.metricNumber}>
                  0.048
                </span>
                <span className={styles.metricUnit}>M/S</span>
              </div>
            </div>
          </div>

          <span className={styles.telemetryDivider} aria-hidden="true" />

          {/* Botón integrado de control de movimiento de la aguja / intensificar electrohilado */}
          <button
            type="button"
            id="action"
            className={styles.telemetryActionBtn}
            data-active={isBoosting}
            onClick={handleBoost}
          >
            <span className={styles.telemetryPulseDot} aria-hidden="true" />
            <span className={styles.telemetryBtnCopy}>
              <span className={styles.telemetryBtnTitle}>
                Intensificar electrohilado
              </span>
              <span className={styles.telemetryBtnSub}>
                {isBoosting ? "IMPULSO 22.8 KV · ACTIVO" : "MODO NOMINAL · 21.4 KV"}
              </span>
            </span>
            <span
              ref={boostFillRef}
              className={styles.telemetryBoostBar}
              aria-hidden="true"
            />
          </button>
        </div>

        {/* Selector translúcido de ángulos de cámara */}
        <div className={styles.camTabs} role="tablist" aria-label="Ángulos de cámara">
          {CAMERA_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={camMode === tab.id}
              className={styles.camBtn}
              data-active={camMode === tab.id}
              onClick={() => handleSelectCamera(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <span className={styles.sceneNumber}>01 / CAD STUDIO</span>
      </div>

      {/* ── FOOTER INFERIOR DE LA HERO ── */}
      <div className={styles.sectionFooter}>
        <span>© 2026 Aya Technologies S.A.C.S. Todos los derechos reservados.</span>
        <span id="metrics" className={styles.srOnly}>
          {metricsText}
        </span>
        <span>ELECTROSPINNING UNIT · ENSAMBLAJE COLECTOR</span>
      </div>
    </section>
  );
}
